import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Err, Ok, Result } from 'ts-results'

import { useNotifications } from 'app/application/application'
import { Form } from 'app/common/presentation'
import {
  DV360Icon,
  FacebookIcon,
  GoogleAdsenseIcon,
  GoogleAnalyticsIcon,
} from 'app/common/presentation/icons'
import { useConnections } from 'app/connections/application'
import {
  AvailableConnectionWithAuthConfig,
  isInstalledConnection,
  isOAuthUser,
} from 'app/connections/types'

import styles from './Authorization.module.scss'
import {
  AuthorizationButton,
  AuthorizationButtonProps,
  AuthorizationData,
} from './AuthorizationButton'

interface AuthorizationProps extends Pick<AuthorizationButtonProps, 'onAuthorizationStateChanged'> {
  connection: AvailableConnectionWithAuthConfig
}

const Authorization = ({ connection }: AuthorizationProps) => {
  const { saveAuthorization } = useConnections()
  const { createNotification, te } = useNotifications()
  const { t } = useTranslation('connections')

  const [authorizationError] = useState<string>()

  const authorizationUser =
    isInstalledConnection(connection) && isOAuthUser(connection.authorizationUser)
      ? connection.authorizationUser
      : null

  const handleAuthorizationSuccess = async (
    authorizationData: AuthorizationData,
  ): Promise<Result<void, string | void>> =>
    saveAuthorization(
      connection,
      connection.config,
      authorizationData.authorizationCode,
      authorizationData.redirectUri,
    ).then((result) => {
      if (result.ok) return Ok(undefined)

      createNotification({
        message: result.val.message,
        title: te(`http.${result.val.code}`),
        type: 'error',
      })
      return Err(result.val.message)
    })

  const getIcon = () => {
    switch (connection.name) {
      case 'dv360':
        return DV360Icon
      case 'facebook':
        return FacebookIcon
      case 'googleAdwords':
        return GoogleAdsenseIcon
      case 'googleAnalytics':
        return GoogleAnalyticsIcon
    }

    return undefined
  }

  const getTitle = () => {
    if (authorizationUser) {
      return t('setup.authorization.account.title')
    }

    return t(`setup.authorization.${connection.name}.title`)
  }

  return (
    <Form.FormRow
      className={styles.authorization}
      description={authorizationUser ? t('setup.authorization.account.description') : undefined}
      icon={authorizationUser ? undefined : getIcon()}
      title={getTitle()}
    >
      <div className={styles.state}>
        {authorizationUser && (
          <div className={styles.authorizationUser}>
            <span>{authorizationUser.name}</span>
            {authorizationUser.email && (
              <span className={styles.authorizationEmail}>{authorizationUser.email}</span>
            )}
          </div>
        )}
        <AuthorizationButton
          connection={connection}
          onAuthorizationSuccess={handleAuthorizationSuccess}
        />
        {authorizationError && <div className={styles.error}>{authorizationError}</div>}
      </div>
    </Form.FormRow>
  )
}

export default Authorization
